/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Button, Col, Form, Row, Typography } from 'antd';
import HistoryChatBoxList from '@/components/list/hitory-chat-box.list';
import { HistoryChatBoxSearch } from '@/components/forms/history-box-chat/search.form';
import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  getChatBoxHistory,
  resetMessages,
  watchChatBoxMessage,
  watchPaginationHistoryChatBox
} from '@/redux/slices/chatbox.slice';
import { setMessages } from '@/redux/slices/app.slice';
import { Message } from '@/enums/message.enum';

export default function HistoryChatBox() {
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const dispatch = useAppDispatch();
  const messageInfo = useAppSelector(watchChatBoxMessage);
  const pagination = useAppSelector(watchPaginationHistoryChatBox);

  const [form] = Form.useForm();

  const loadChatHistory = useCallback(
    (page: number, filter?: ChatHistoryFilter, isLoadMore = false) => {
      if (isLoadMore) {
        setIsLoadingMore(true);
      }

      dispatch(
        getChatBoxHistory({
          offset: page,
          limit: 5,
          ...(filter?.from && { startDate: filter.from }),
          ...(filter?.to && { endDate: filter.to }),
          ...(filter?.keyword && { search: filter.keyword })
        })
      ).finally(() => {
        if (isLoadMore) {
          setIsLoadingMore(false);
        }
      });
    },
    [dispatch]
  );

  const handleSearch = useCallback(
    (newFilter: ChatHistoryFilter) => {
      setCurrentPage(1);
      loadChatHistory(1, newFilter, false);
    },
    [loadChatHistory]
  );

  const handleLoadMore = useCallback(() => {
    const values = form.getFieldsValue();

    const startAt = values.timeRange?.[0]
      ? values.timeRange[0].toISOString()
      : undefined;
    const endAt = values.timeRange?.[1]
      ? values.timeRange[1].toISOString()
      : undefined;

    const newFilter: ChatHistoryFilter = {
      from: startAt,
      to: endAt,

      keyword: values.keyword || ''
    };
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    loadChatHistory(nextPage, newFilter, true);
  }, [currentPage, loadChatHistory, form]);

  useEffect(() => {
    loadChatHistory(1);
  }, [loadChatHistory]);

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo?.description
          }
        ])
      );
      if (messageInfo.type === Message.SUCCESS) {
        dispatch(resetMessages());
      }
    }
  }, [dispatch, messageInfo]);

  return (
    <div css={rootStyles}>
      <Row css={boxStyles} justify='space-between' align='middle'>
        <Col>
          <Typography.Title css={titleStyles} level={5}>
            Lịch sử hội thoại ChatGPT
          </Typography.Title>
        </Col>
      </Row>

      <div css={boxStyles}>
        <HistoryChatBoxSearch form={form} onSearch={handleSearch} />
      </div>

      <div css={boxStyles}>
        <HistoryChatBoxList />

        {pagination && currentPage < pagination.totalPage && (
          <div css={loadMoreContainerStyles}>
            <Button
              type='primary'
              size='large'
              loading={isLoadingMore}
              onClick={handleLoadMore}
              css={loadMoreButtonStyles}
            >
              {isLoadingMore ? 'Đang tải...' : 'Tải thêm'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

const rootStyles = css`
  background: var(--gray-soft-color);
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  padding: 1.4rem 2.4rem;
  min-height: calc(100vh - var(--header-height));
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const titleStyles = css`
  margin-bottom: 0 !important;
  line-height: 3.2rem !important;
`;

const loadMoreContainerStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #f0f0f0;
`;

const loadMoreButtonStyles = css`
  min-width: 150px;
  height: 40px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
`;
